import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { askGPT } from '../components/OpenAIService';
import { ArrowRight, Target, Clipboard, BookOpen, ThumbsUp, ArrowLeft } from 'lucide-react';

// Import des composants modulaires
import ObjectifEditor from './ObjectifEditor';
import ObjectifConversation from './ObjectifConversation';
import ObjectifFinalizeButton from './ObjectifFinalizeButton';
import ObjectifUpdater from './ObjectifUpdater';
import { analyzeObjectifThemeAndChapter } from './ObjectifAnalyzer';

const ObjectifDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // États initiaux
  const initialObjectif = location.state?.objectif || '';
  const objectifType = location.state?.type || 'project';
  
  // États du composant
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [finalObjectif, setFinalObjectif] = useState(initialObjectif);
  const [userId, setUserId] = useState(null);
  const [savedCompetenceId, setSavedCompetenceId] = useState(null);
  const [readyForNextStep, setReadyForNextStep] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [error, setError] = useState(null);
  
  // Références
  const conversationStarted = useRef(false);

  // Vérification de l'objectif initial
  useEffect(() => {
    if (!initialObjectif || initialObjectif.trim() === '') {
      navigate('/formulation-objectif');
    }
  }, [initialObjectif, navigate]);

  // Gestion de l'authentification
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        navigate('/login');
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);

  // Démarrer la conversation initiale
  const startConversation = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const systemPrompt = `
Tu es un coach, conseiller spécialisé en développement personnel, éducatif et professionnel.
Ton rôle est d'aider l'utilisateur à clarifier et préciser son objectif.

Objectif actuel : "${finalObjectif}"
Type d'objectif : ${objectifType}

Pose une question pertinente et concise qui aidera l'utilisateur à préciser son objectif.
`;
      
      const initialMessage = await askGPT(systemPrompt);
      
      const fallbackMessage = `Bonjour ! Je vais vous aider à préciser votre objectif : "${finalObjectif}". Pouvez-vous me donner plus de détails ?`;
      
      setMessages([{
        id: `bot-${Date.now()}`,
        type: 'bot',
        text: initialMessage || fallbackMessage
      }]);
    } catch (error) {
      console.error('Erreur au démarrage de la conversation:', error);
      setError('Impossible de démarrer la conversation');
      
      setMessages([{
        id: `bot-${Date.now()}`,
        type: 'bot',
        text: `Bonjour ! Je suis là pour vous aider à préciser votre objectif. Parlons de "${finalObjectif}".`
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [finalObjectif, objectifType]);

  // Initialiser la conversation
  useEffect(() => {
    if (!conversationStarted.current && initialObjectif && userId) {
      conversationStarted.current = true;
      startConversation();
    }
  }, [initialObjectif, userId, startConversation]);

  // Gérer l'envoi de message
  const handleSendMessage = useCallback(async (userMessage) => {
    if (!userMessage || isLoading) return;
    
    setIsLoading(true);
    
    // Ajouter le message de l'utilisateur
    const updatedMessages = [
      ...messages,
      { id: `user-${Date.now()}`, type: 'user', text: userMessage }
    ];
    setMessages(updatedMessages);
    
    try {
      // Construire l'historique de conversation
      const conversationHistory = updatedMessages
        .map(m => `${m.type === 'bot' ? 'Assistant' : 'Utilisateur'}: ${m.text}`)
        .join('\n');
      
      // Préparer le prompt pour GPT
      const prompt = `
Contexte : Aide l'utilisateur à préciser son objectif : "${finalObjectif}"
Type d'objectif : ${objectifType}

Historique de conversation :
${conversationHistory}

Réponds de manière pertinente à aux questions de l'utilisateur et terminer en posant une question qui aide à clarifier l'objectif.
`;
      
      // Vérifier si l'utilisateur semble satisfait
      const satisfactionKeywords = ['satisfait', 'ça me convient', 'c\'est bon', 'parfait', 'ok'];
      const isUserSatisfied = satisfactionKeywords.some(keyword => 
        userMessage.toLowerCase().includes(keyword)
      );
      
      if (isUserSatisfied) {
        setReadyForNextStep(true);
        const confirmMessage = `Super ! Votre objectif semble bien défini. Voulez-vous passer à l'étape suivante ?`;
        
        setMessages([
          ...updatedMessages,
          { id: `bot-${Date.now()}`, type: 'bot', text: confirmMessage }
        ]);
        setIsLoading(false);
        return;
      }
      
      // Obtenir la réponse de l'assistant
      const botResponse = await askGPT(prompt);
      
      setMessages([
        ...updatedMessages,
        { id: `bot-${Date.now()}`, type: 'bot', text: botResponse }
      ]);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setMessages(prevMessages => [
        ...prevMessages,
        { 
          id: `bot-error-${Date.now()}`, 
          type: 'bot', 
          text: "Désolé, j'ai rencontré un problème. Pouvez-vous reformuler ?" 
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, finalObjectif, objectifType]);

  // Mettre à jour manuellement l'objectif
  const handleManualUpdateObjectif = useCallback(async (updatedObjectif) => {
    if (!updatedObjectif.trim()) return;
    
    setFinalObjectif(updatedObjectif);
    setLastUpdateTime(new Date().toLocaleTimeString());
    
    // Mettre à jour dans Firestore si déjà sauvegardé
    if (savedCompetenceId && userId) {
      try {
        await updateDoc(doc(db, 'users', userId, 'competences', savedCompetenceId), {
          title: updatedObjectif,
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Erreur de mise à jour:', error);
      }
    }
    
    // Relancer la conversation avec le nouvel objectif
    try {
      const prompt = `
L'utilisateur a modifié son objectif pour : "${updatedObjectif}"
Pose une question pertinente qui l'aidera à préciser davantage.
`;
      
      const botResponse = await askGPT(prompt);
      
      setMessages(prevMessages => [
        ...prevMessages,
        { 
          id: `bot-edit-${Date.now()}`, 
          type: 'bot', 
          text: botResponse || "Je vois que vous avez modifié votre objectif. Pouvez-vous m'en dire plus ?"
        }
      ]);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    }
  }, [savedCompetenceId, userId]);

  // Finaliser l'objectif
  const finalizeObjectif = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    
    try {
      // Analyser le thème et le chapitre
      const themeAnalysis = await analyzeObjectifThemeAndChapter(
        finalObjectif, 
        objectifType
      );
      
      // Créer l'objectif dans Firestore
      const competenceRef = await addDoc(collection(db, 'users', userId, 'competences'), {
        title: finalObjectif,
        shortTitle: finalObjectif.substring(0, 40) + (finalObjectif.length > 40 ? '...' : ''),
        details: messages.map(m => `${m.type}: ${m.text}`).join('\n'),
        theme: themeAnalysis.theme,
        chapitre: themeAnalysis.chapitre,
        type: objectifType,
        phase: "ambition",
        phaseProgress: 100,
        globalProgress: 30,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastAccessedAt: serverTimestamp(),
        conversationHistory: messages
      });
      
      setSavedCompetenceId(competenceRef.id);
      
      // Message de confirmation
      setMessages(prevMessages => [
        ...prevMessages,
        { 
          id: `bot-final-${Date.now()}`, 
          type: 'bot', 
          text: `Objectif enregistré ! Thème : ${themeAnalysis.theme}, Chapitre : ${themeAnalysis.chapitre}`
        }
      ]);
      
      // Redirection différée
      setTimeout(() => {
        navigate(`/Recommandations/${competenceRef.id}`, { 
          state: { 
            competence: {
              id: competenceRef.id,
              title: finalObjectif,
              theme: themeAnalysis.theme,
              chapitre: themeAnalysis.chapitre,
              type: objectifType
            }
          }
        });
      }, 2000);
    } catch (error) {
      console.error('Erreur de finalisation:', error);
      setError('Impossible de finaliser l\'objectif');
    } finally {
      setIsLoading(false);
    }
  }, [userId, finalObjectif, objectifType, messages, navigate]);

  // Rendu du composant
  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <header className="bg-white shadow-sm py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold text-purple-700 flex items-center">
            <Target className="h-6 w-6 mr-2" /> 
            Précisez votre objectif
          </h1>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/formulation-objectif')}
              className="flex items-center text-gray-600 hover:text-purple-700"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              <span>Retour</span>
            </button>
            <button 
              onClick={() => navigate('/competencesList')}
              className="flex items-center text-gray-600 hover:text-purple-700"
            >
              <Clipboard className="h-5 w-5 mr-1" />
              <span>Mes objectifs</span>
            </button>
          </div>
        </div>
      </header>
      
      <div className="flex-1 max-w-7xl mx-auto w-full p-4 md:p-6 flex flex-col">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 mb-4">
            {error}
          </div>
        )}

        <ObjectifEditor 
          objectif={finalObjectif}
          objectifType={objectifType}
          onSave={handleManualUpdateObjectif}
          isReadOnly={savedCompetenceId !== null}
          lastUpdate={lastUpdateTime}
        />
        
        {!savedCompetenceId && lastUpdateTime && (
          <div className="mb-4 flex items-center text-xs text-gray-500">
            <div className="w-2 h-2 bg-purple-400 rounded-full mr-2"></div>
            <span>
              Objectif actualisé à {lastUpdateTime}
            </span>
          </div>
        )}
        
        <div className="hidden">
          <ObjectifUpdater
            initialObjective={finalObjectif}
            conversationHistory={messages}
            onObjectiveUpdate={handleManualUpdateObjectif}
            objectiveType={objectifType}
          />
        </div>
        
        <div className="flex-1 flex flex-col">
          <ObjectifConversation
            messages={messages}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            disabled={savedCompetenceId !== null}
          />
          
          <div className="mt-4 flex flex-col sm:flex-row gap-4">
            {readyForNextStep && !savedCompetenceId && (
              <button
                onClick={finalizeObjectif}
                disabled={isLoading}
                className="flex-1 p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg flex items-center justify-center hover:from-green-600 hover:to-green-700"
              >
                <ThumbsUp className="h-5 w-5 mr-2" />
                Valider et obtenir des recommandations
              </button>
            )}
            
            {!readyForNextStep && !savedCompetenceId && messages.length > 3 && (
              <ObjectifFinalizeButton
                onClick={finalizeObjectif}
                isLoading={isLoading}
                isDisabled={savedCompetenceId !== null}
              />
            )}
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto w-full p-4 md:p-6">
        <button
          onClick={() => navigate('/competencesList')}
          className="flex items-center justify-center w-full p-3 bg-gradient-to-r from-blue-50 to-purple-50 text-purple-700 rounded-lg hover:shadow-md transition-shadow"
        >
          Voir tous mes objectifs <ArrowRight className="ml-2 h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default ObjectifDetail;